/**
 * Adaptive quality controller.
 *
 * Tracks an exponential moving average of frame time. Downgrades one tier
 * when the EMA stays above target * downgradeRatio for downgradeFrames
 * consecutive frames. Upgrades one tier when it stays below
 * target * upgradeRatio for upgradeFrames frames. After any change the
 * governor ignores cooldownFrames frames.
 *
 * Hysteresis comes from three places: the gap between the two ratios, the
 * asymmetric frame counts (quick to shed load, slow to add it), and the
 * cooldown. Together they stop the tier flapping around the target.
 *
 * Probe backoff (added after the first real-device calibration): the
 * upgrade signal is work time, which can understate a tier's true cost (on
 * one Windows PC, ultra measured ~3 ms of CPU + GPU work yet ran at 33 ms
 * per frame; see bench/calibration/). An upgrade is a probe. If the
 * governor has to downgrade out of the new tier within failWindowFrames,
 * the probe failed: that tier is blocked for a backoff period, which
 * doubles on each consecutive failure up to maxBackoffFrames. A probe that
 * holds resets the backoff. The governor still retries occasionally, in
 * case the slowdown was temporary (thermal throttling, a background tab).
 *
 * Dropped-frame trigger (added after the second Inspiron sweep): the EMA
 * mixes ~2 ms work-time readings from on-time frames with ~33 ms interval
 * readings from late ones. With about half the frames late, the EMA sits
 * near 16 to 18 ms, under the downgrade threshold, while the user sees
 * ~40 fps stutter. So the governor also tracks the fraction of frames that
 * missed a refresh (an EMA of a 0/1 indicator). Above maxDropRate it counts
 * as slow, exactly like a high EMA, so a stuttering probe fails and backs
 * off. maxDropRate = 5% is a judgment of visible stutter, not a measured
 * threshold.
 */
import { TIER_ORDER, tierIndex, type TierId } from '../config/tiers';

export interface GovernorOptions {
  targetFrameMs: number;
  emaAlpha: number;
  downgradeRatio: number;
  downgradeFrames: number;
  upgradeRatio: number;
  upgradeFrames: number;
  cooldownFrames: number;
  /** A downgrade this soon after an upgrade marks the upgrade as failed. */
  failWindowFrames: number;
  /** First block period after a failed upgrade. Doubles per repeat failure. */
  probeBackoffFrames: number;
  maxBackoffFrames: number;
  /** Smoothing for the dropped-frame rate (about 1 / window in frames). */
  dropRateAlpha: number;
  /** Dropped-frame rate above which the tier counts as too slow. */
  maxDropRate: number;
  /** Upgrades also require the dropped-frame rate below this. */
  upgradeMaxDropRate: number;
}

export interface GovernorLogEntry {
  frame: number;
  from: string;
  to: string;
  emaMs: number;
}

export const DEFAULT_GOVERNOR_OPTIONS: Readonly<GovernorOptions> = {
  targetFrameMs: 1000 / 60,
  emaAlpha: 0.1,
  downgradeRatio: 1.15,
  downgradeFrames: 30,
  upgradeRatio: 0.7,
  upgradeFrames: 180,
  cooldownFrames: 120,
  failWindowFrames: 600, // 10 s at 60 fps
  probeBackoffFrames: 1_800, // 30 s
  maxBackoffFrames: 28_800, // 8 min
  dropRateAlpha: 1 / 60, // about a 1 s window
  maxDropRate: 0.05,
  upgradeMaxDropRate: 0.01,
};

const LOG_CAPACITY = 50;

export class Governor {
  private readonly opts: GovernorOptions;
  private tierIdx: number;
  private emaMs: number;
  private frame = 0;
  private slowRun = 0;
  private fastRun = 0;
  private cooldown = 0;
  // Probe backoff state.
  private lastUpgradeFrame = Number.NEGATIVE_INFINITY;
  private lastUpgradeIdx = -1;
  private blockedIdx = Number.POSITIVE_INFINITY;
  private blockedUntil = 0;
  private backoff: number;
  private dropRate = 0;

  // Ring buffer of preallocated entries, mutated in place.
  private readonly ring: GovernorLogEntry[];
  private ringHead = 0;
  private ringSize = 0;
  private readonly ordered: GovernorLogEntry[] = [];

  constructor(initial: TierId, opts?: Partial<GovernorOptions>) {
    this.opts = { ...DEFAULT_GOVERNOR_OPTIONS, ...opts };
    this.tierIdx = tierIndex(initial);
    this.emaMs = this.opts.targetFrameMs;
    this.backoff = this.opts.probeBackoffFrames;
    this.ring = [];
    for (let i = 0; i < LOG_CAPACITY; i++) this.ring.push({ frame: 0, from: '', to: '', emaMs: 0 });
  }

  get tier(): TierId {
    return TIER_ORDER[this.tierIdx] as TierId;
  }

  get ema(): number {
    return this.emaMs;
  }

  /** Tier currently blocked from upgrades after a failed probe, or null. */
  get blockedTier(): TierId | null {
    if (this.frame >= this.blockedUntil || !Number.isFinite(this.blockedIdx)) return null;
    return TIER_ORDER[this.blockedIdx] as TierId;
  }

  /** Frames until the block lifts (0 if none). */
  get blockedForFrames(): number {
    return this.frame < this.blockedUntil ? this.blockedUntil - this.frame : 0;
  }

  /** Smoothed fraction of recent frames that missed a refresh. */
  get droppedFrameRate(): number {
    return this.dropRate;
  }

  /**
   * Feed one frame. frameMs is the governor signal (see core/gpuTiming.ts).
   * missedDeadline: this frame's interval exceeded about 1.5 refreshes.
   * Returns the new tier on change, else null.
   */
  observe(frameMs: number, missedDeadline = false): TierId | null {
    this.frame++;
    this.emaMs += this.opts.emaAlpha * (frameMs - this.emaMs);
    this.dropRate += this.opts.dropRateAlpha * ((missedDeadline ? 1 : 0) - this.dropRate);

    // An upgrade that survives the fail window counts as a success.
    if (
      this.lastUpgradeIdx === this.tierIdx &&
      this.frame - this.lastUpgradeFrame === this.opts.failWindowFrames + 1
    ) {
      this.backoff = this.opts.probeBackoffFrames;
    }

    if (this.cooldown > 0) {
      this.cooldown--;
      return null;
    }

    const target = this.opts.targetFrameMs;
    const slow =
      this.emaMs > target * this.opts.downgradeRatio || this.dropRate > this.opts.maxDropRate;
    const fast =
      this.emaMs < target * this.opts.upgradeRatio && this.dropRate < this.opts.upgradeMaxDropRate;
    this.slowRun = slow ? this.slowRun + 1 : 0;
    this.fastRun = fast ? this.fastRun + 1 : 0;

    if (this.slowRun >= this.opts.downgradeFrames && this.tierIdx > 0) {
      const failedProbe =
        this.lastUpgradeIdx === this.tierIdx &&
        this.frame - this.lastUpgradeFrame <= this.opts.failWindowFrames;
      if (failedProbe) {
        this.blockedIdx = this.tierIdx;
        this.blockedUntil = this.frame + this.backoff;
        this.backoff = Math.min(this.backoff * 2, this.opts.maxBackoffFrames);
      }
      this.lastUpgradeIdx = -1;
      return this.change(this.tierIdx - 1);
    }
    const nextIdx = this.tierIdx + 1;
    const blocked = nextIdx >= this.blockedIdx && this.frame < this.blockedUntil;
    if (this.fastRun >= this.opts.upgradeFrames && nextIdx < TIER_ORDER.length && !blocked) {
      this.lastUpgradeFrame = this.frame;
      this.lastUpgradeIdx = nextIdx;
      return this.change(nextIdx);
    }
    return null;
  }

  /** Jump to a tier without logging, e.g. after the startup benchmark. */
  reset(tier: TierId): void {
    this.tierIdx = tierIndex(tier);
    this.slowRun = 0;
    this.fastRun = 0;
    this.cooldown = this.opts.cooldownFrames;
  }

  /** Oldest first. The returned array is reused between calls. */
  get log(): ReadonlyArray<GovernorLogEntry> {
    this.ordered.length = this.ringSize;
    const start = (this.ringHead - this.ringSize + LOG_CAPACITY) % LOG_CAPACITY;
    for (let i = 0; i < this.ringSize; i++) {
      this.ordered[i] = this.ring[(start + i) % LOG_CAPACITY] as GovernorLogEntry;
    }
    return this.ordered;
  }

  private change(nextIdx: number): TierId {
    const entry = this.ring[this.ringHead] as GovernorLogEntry;
    entry.frame = this.frame;
    entry.from = TIER_ORDER[this.tierIdx] as TierId;
    entry.to = TIER_ORDER[nextIdx] as TierId;
    entry.emaMs = this.emaMs;
    this.ringHead = (this.ringHead + 1) % LOG_CAPACITY;
    if (this.ringSize < LOG_CAPACITY) this.ringSize++;

    this.tierIdx = nextIdx;
    this.slowRun = 0;
    this.fastRun = 0;
    // Judge the new tier on its own frames, not the old tier's drops.
    this.dropRate = 0;
    this.cooldown = this.opts.cooldownFrames;
    return entry.to as TierId;
  }
}
